import apiClient from './apiClient';

// ============================================================
// Types -- matching backend Go models (FJ610 master, FJ611 detail)
// ============================================================

export interface FJ610 {
  fj610_01: string;          // 加工單號 (PK)
  fj610_11: string | null;   // 加工日期
  fj610_12: string;          // 公司代號
  fj610_13: string;          // 產品主碼
  fj610_131: string;         // 產品副碼
  fj610_14: string;          // 工繳代號
  fj610_15: string | null;   // 預計交貨日
  fj610_16: string;          // 包裝代號
  fj610_17: number;          // 總託工數量 (計算)
  fj610_18: string;          // 備註代號
  fj610_19: string;          // 備註內容
  fj610_20: string;          // 附註
  fj610_21: string;          // 單位
  fj610_22: number;          // 金額合計 (計算)
  fj610_23: number;          // 明細總項數
  fj610_24: number;          // 計價方式 (1=計量 2=計缸)
  fj610_30: string;          // 幣別
  fj610_48: number;          // 加工類別 (1=織造 2=染整 3=防水透濕)
  fj610_50: number;          // 織造子類型
  fj610_61: string;          // 染色代碼
  fj610_62: string;          // 定型代碼
  fj610_621: string;         // 定型複選1
  fj610_622: string;         // 定型複選2
  fj610_623: string;         // 定型複選3
  fj610_624: string;         // 定型複選4
  fj610_63: string;          // 印染代碼
  fj610_64: string;          // 針織加工代碼
  fj610_641: string;         // 針織複選1
  fj610_642: string;         // 針織複選2
  fj610_643: string;         // 針織複選3
  fj610_644: string;         // 針織複選4
  fj610_65: string;          // 其它代碼
  fj610_651: string;         // 其它複選1
  fj610_652: string;         // 其它複選2
  fj610_653: string;         // 其它複選3
  fj610_654: string;         // 其它複選4
  fj610_70: number;          // 防水透濕類型
  fj610_71: string;          // 塗佈代碼
  fj610_72: string;          // 貼合代碼1
  fj610_73: string;          // 貼合代碼2
  fj610_74: string;          // 貼合代碼3
  fj610_75: number;          // 防水要求
  fj610_76: number;          // 防水度
  fj610_77: number;          // 透濕度
  fj610_80: string;          // 審核狀態
  fj610_91: string;          // 入單人員
  fj610_92: string | null;   // 修改日期
  fj610_93: string;          // 修改人員

  details?: FJ611[];
}

export interface FJ611 {
  fj611_01: string;          // 加工單號 (FK)
  fj611_02: number;          // 項次
  fj611_11: string;          // 產品主碼
  fj611_111: string;         // 產品副碼
  fj611_12: string;          // 色號
  fj611_121: string;         // 色名
  fj611_12a: string;         // CBNO代號
  fj611_14: number;          // 成品量
  fj611_15: number;          // 未稅單價
  fj611_151: number;         // 含稅單價
  fj611_16: number;          // 未稅金額
  fj611_161: number;         // 含稅金額
  fj611_17: string;          // 產品規格
}

export interface CreateRequest {
  master: Partial<FJ610>;
  details: Partial<FJ611>[];
}

export interface ListResponse {
  data: FJ610[];
  total: number;
  page: number;
  size: number;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface ApprovalResponse {
  request_no: string;
  action: string;
  old_status: string;
  new_status: string;
  message: string;
}

export interface ApprovalHistory {
  id: number;
  request_no: string;
  action: string;
  actor_id: string;
  actor_name: string;
  old_status: string;
  new_status: string;
  comment: string;
  created_at: string;
}

// ============================================================
// API methods
// ============================================================

export const rdSubcontractApi = {
  async list(page = 1, size = 100, keyword?: string): Promise<ListResponse> {
    const params: Record<string, string | number> = { page, size };
    if (keyword) params.keyword = keyword;
    const res = await apiClient.get('/api/rd-subcontract', { params });
    return res.data;
  },

  async get(orderNo: string): Promise<FJ610> {
    const res = await apiClient.get(`/api/rd-subcontract/${encodeURIComponent(orderNo)}`);
    return res.data;
  },

  async create(data: CreateRequest): Promise<FJ610> {
    const res = await apiClient.post('/api/rd-subcontract', data);
    return res.data;
  },

  async update(orderNo: string, data: CreateRequest): Promise<FJ610> {
    const res = await apiClient.put(`/api/rd-subcontract/${encodeURIComponent(orderNo)}`, data);
    return res.data;
  },

  async delete(orderNo: string): Promise<void> {
    await apiClient.delete(`/api/rd-subcontract/${encodeURIComponent(orderNo)}`);
  },

  async submitForApproval(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/rd-subcontract/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async approve(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/rd-subcontract/${encodeURIComponent(id)}/approve`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/rd-subcontract/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/rd-subcontract/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<ApprovalHistory[]> {
    const res = await apiClient.get(`/api/rd-subcontract/${encodeURIComponent(id)}/approval-history`);
    return res.data.data || [];
  },
};
